export const PROFILE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const PROFILE_IMAGE_TOO_LARGE =
	"This photo is too large. Maximum size is 5 MB. Please use JPEG, PNG, GIF, WebP, or HEIF.";
export const PROFILE_IMAGE_UNREADABLE =
	"Could not read the selected photo. If it is stored in iCloud, open it in Photos first to download it, then try again.";

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|heic|heif)$/i;

function extensionForMime(mime: string): string {
	if (mime === "image/png") return ".png";
	if (mime === "image/webp") return ".webp";
	if (mime === "image/gif") return ".gif";
	if (mime === "image/heic" || mime === "image/heif") return ".heic";
	return ".jpg";
}

function sniffMime(bytes: Uint8Array): string | null {
	if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
		return "image/jpeg";
	}
	if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
		return "image/png";
	}
	if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
		return "image/gif";
	}
	if (
		bytes.length >= 12 &&
		bytes[0] === 0x52 &&
		bytes[1] === 0x49 &&
		bytes[2] === 0x46 &&
		bytes[3] === 0x46 &&
		bytes[8] === 0x57 &&
		bytes[9] === 0x45 &&
		bytes[10] === 0x42 &&
		bytes[11] === 0x50
	) {
		return "image/webp";
	}
	if (bytes.length >= 12) {
		const box = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
		const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]).toLowerCase();
		if (box === "ftyp" && ["heic", "heix", "hevc", "hevx", "mif1", "msf1"].includes(brand)) {
			return "image/heic";
		}
	}
	return null;
}

function buildProfileImageFilename(
	input: File | Blob,
	bytes: Uint8Array,
	fallbackBase: string,
): { filename: string; mime: string } {
	const mime = sniffMime(bytes) || input.type || "image/jpeg";
	const ext = extensionForMime(mime);
	const originalName = input instanceof File ? input.name?.trim() : "";
	const rawName = originalName || `${fallbackBase}${ext}`;
	const safeName = rawName.replace(/[^\w.\- ()]/g, "_");
	const withExt = IMAGE_EXT.test(safeName) ? safeName : `${safeName}${ext}`;
	const filename = withExt.replace(IMAGE_EXT, ext);
	return { filename, mime };
}

/**
 * Copy photo bytes into memory, fix iOS filenames, and reject photos over 5 MB.
 * iPad Safari can invalidate picker File handles after async gaps, so callers
 * should await this immediately after file selection and before upload.
 */
export async function prepareProfileImageFile(input: File | Blob, fallbackBase: string): Promise<File> {
	const bytes = new Uint8Array(await input.arrayBuffer());
	if (bytes.length === 0) {
		throw Object.assign(new Error(PROFILE_IMAGE_UNREADABLE), { status: 400 });
	}

	const { filename, mime } = buildProfileImageFilename(input, bytes, fallbackBase);
	const file = new File([bytes], filename, { type: mime });

	if (file.size > PROFILE_IMAGE_MAX_BYTES) {
		throw Object.assign(new Error(PROFILE_IMAGE_TOO_LARGE), { status: 413 });
	}
	return file;
}
